/**
 * All requests — the org-wide list, backed by the ENGINE rows.
 *
 * WHY THIS FILE REPLACED A SECOND LIST (owner, 2026-09-01, ADR-046)
 * ----------------------------------------------------------------
 * `/processes/requests/history` and `/processes/instances` were two nav entries showing the SAME
 * business objects through two internal models. `sync_request_from_instance` mirrors instance →
 * request 1:1 (`completed`→`completed`, `withdrawn`→`withdrawn`, `failed`→`failed`, the three active
 * instance states → `in_review`), so every row on one page had a counterpart on the other.
 *
 * They differed only in which statuses each chose to render, and that difference was a DEFECT: the
 * page titled "All requests" listed 8 statuses and omitted `withdrawn` and `failed`, because
 * `RequestList` filters rows with `statuses.includes(r.status)`. An admin looking for a request
 * somebody had taken back found nothing and concluded it was deleted.
 *
 * WHY THE ENGINE LIST IS THE SURVIVOR, measured rather than assumed
 * ----------------------------------------------------------------
 * The merge looked like a union at first, because `RequestList` renders inline approve / reject /
 * delegate buttons and `InstanceList` does not. It is not: `RequestHistory` passed
 * `allowedActions={[]}`. Those buttons only ever appeared on `PendingApprovals`
 * (`allowedActions={['approve','reject','delegate']}`), which is untouched.
 *
 * So `InstanceList` is a strict superset of what this page showed:
 *
 *   | column        | old (RequestList) | new (InstanceList) |
 *   |---------------|-------------------|--------------------|
 *   | request       | ✓                 | ✓ (number + definition name) |
 *   | requester     | ✓                 | ✓ (nested actor, so a departed filer still has a name) |
 *   | approver      | ✓                 | ✓ (assignee, blank once the request is over) |
 *   | current step  | —                 | ✓ |
 *   | priority      | ✓                 | ✓ |
 *   | status        | ✓ (8 of 10)       | ✓ (all) |
 *   | when          | ✓ Age             | ✓ Started |
 *   | actions       | view              | view + cancel + retry |
 *
 * `Age` for `Started` is the one deliberate trade: an absolute timestamp is what an admin quotes
 * when they chase a request, and the relative form is on the detail page.
 *
 * `/processes/instances` stays routed as a redirect here — it is linked from the Setup Guide, from
 * ProcessOverview's Recent activity card and from notification deep links, and a link that 404s
 * because a page was tidied is worse than the untidiness (svdy-ui §12.10).
 */
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@mui/material';
import { AddOutlined } from '@mui/icons-material';

import InstanceList, { InstanceSummaryCard } from '../components/InstanceList';
import FileRequestForWorkerDialog from './FileRequestForWorkerDialog';
import { processesApi } from '../../../api';
import type { ProcessInstance } from '../../../api/processes';
import {
  ACTIVE_INSTANCE_STATUSES,
  FAILED_INSTANCE_STATUSES,
  HISTORY_INSTANCE_STATUSES,
  STATUS_GROUPS,
  type StatusGroup,
} from '../ProcessInstances/statusGroups';

const AllRequests: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const requested = searchParams.get('status') ?? '';
  const initialStatusGroup: StatusGroup = Object.prototype.hasOwnProperty.call(STATUS_GROUPS, requested)
    ? (requested as StatusGroup)
    : 'all';

  /**
   * NOTIFICATION DEEP LINK, carried over from the page this replaced.
   *
   * `notificationTarget.ts` turns a backend `link` into
   * `/processes/requests/history?openId=prq_…`, and the old list opened that request in a drawer.
   * Dropping it would have made every process notification land on a list that does not open
   * anything — the §12.10 defect class, arriving through a query parameter instead of a route.
   *
   * The id is a REQUEST id and this page lists INSTANCES, so it cannot be passed through: the
   * request is fetched for its `instance_id` and we navigate to the instance detail page. That is
   * the fuller surface anyway — the drawer showed a subset of it.
   *
   * `useRef` latch, not a boolean state: the same `openId` must not re-fire on re-render while a
   * DIFFERENT one still must (session-discipline §11.2). Failure is deliberately silent — a stale
   * notification for a deleted request should leave the admin on a working list, not on an error.
   */
  const openId = searchParams.get('openId');
  const lastOpenedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!openId || lastOpenedRef.current === openId) return;
    lastOpenedRef.current = openId;
    let cancelled = false;
    processesApi.processRequestsApi.get(openId)
      .then((request) => {
        const instanceId = (request as { instance_id?: string | null })?.instance_id;
        if (!cancelled && instanceId) navigate(`/processes/instances/${instanceId}`, { replace: true });
      })
      .catch(() => { /* stale or deleted request: stay on the list */ });
    return () => { cancelled = true; };
  }, [openId, navigate]);

  const buildSummary = (rows: ProcessInstance[]): InstanceSummaryCard[] => {
    const count = (group: readonly string[]) =>
      rows.filter((r) => group.includes(r.status ?? '')).length;
    return [
      { id: 'total', title: t('processes:instances.summary.totalInstances'), value: String(rows.length), color: 'primary' },
      { id: 'active', title: t('processes:instances.summary.active'), value: String(count(ACTIVE_INSTANCE_STATUSES)), color: 'info' },
      { id: 'history', title: t('processes:instances.summary.history'), value: String(count(HISTORY_INSTANCE_STATUSES)), color: 'success' },
      { id: 'failed', title: t('processes:instances.summary.totalFailed'), value: String(count(FAILED_INSTANCE_STATUSES)), color: 'error' },
    ];
  };

  // FILE FOR A WORKER — the entry the module had no button for (owner 2026-09-05). The capability is

  // backend-gated on recording who typed it (alembic 120), so the dialog also says so on screen.

  const [fileOpen, setFileOpen] = React.useState(false);

  const [reloadKey, setReloadKey] = React.useState(0);


  return (
    <>
    <InstanceList
      key={reloadKey}
      headerAction={(
        <Button
          size="small"
          variant="contained"
          startIcon={<AddOutlined fontSize="small" />}
          onClick={() => setFileOpen(true)}
          sx={{ mr: 1 }}
        >
          {t('processes:requests.fileForWorker.action')}
        </Button>
      )}
      title={t('processes:instances.wrappers.all')}
      description={t('processes:instances.wrappers.allDesc')}
      initialStatusGroup={initialStatusGroup}
      buildSummary={buildSummary}
    />
    <FileRequestForWorkerDialog
      open={fileOpen}
      onClose={() => setFileOpen(false)}
      onFiled={() => setReloadKey((k) => k + 1)}
    />
    </>
  );
};

export default AllRequests;
